import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatTime } from '@/lib/datetime'

import { AdminHeader } from '../AdminLayout'
import { CakeMomentPanel } from '../components/CakeMomentPanel'
import { CollectionEditor, FormActions } from '../components/CollectionEditor'
import {
  I18nField,
  NumberField,
  SelectField,
  SwitchField,
  TextField,
  type I18nValue,
} from '../components/fields'
import { ScreenLinkCard } from '../components/ScreenLinkCard'
import { useAdminContext } from '../session'
import { useCollection } from '../useCollection'
import type { Activity, I18n, ProgramItem, ProgramItemKind } from '../types'

const KINDS: ProgramItemKind[] = [
  'slot',
  'activity',
  'competition_round',
  'cake_moment',
  'award_reveal',
  'custom',
]

/**
 * Le déroulé de la soirée, et les animations qui l'alimentent.
 *
 * Deux collections distinctes, et la distinction est la clé de l'écran :
 *
 * * **les activités** sont un catalogue — les jeux, les animations, avec
 *   leurs règles et leur matériel. Elles existent hors du temps.
 * * **les étapes** sont l'ordre du soir. Une étape peut pointer vers une
 *   activité, un tour de concours ou une remise de prix.
 *
 * Ce qui n'est PAS ici : le statut d'une étape (en cours, terminée). Il se
 * pilote depuis `/ops/deroule`, au micro, et se calcule côté serveur. Le
 * saisir à la main depuis un back-office ferait diverger l'écran de la salle
 * de ce qui se passe réellement.
 */
export function AdminProgramPage() {
  const { t, i18n } = useTranslation()
  const { eventId, event, can } = useAdminContext()

  useEffect(() => {
    document.title = t('admin.section.program')
  }, [t])

  const activities = useCollection<Activity>({
    key: 'admin-activities',
    path: `/admin/events/${eventId}/activities`,
    eventId,
    related: [['admin-readiness', eventId ?? '']],
  })
  const items = useCollection<ProgramItem>({
    key: 'admin-program-items',
    path: `/admin/events/${eventId}/program-items`,
    eventId,
    related: [['program'], ['admin-readiness', eventId ?? '']],
  })

  function label(value: I18n): string {
    return value[i18n.language] || value[Object.keys(value)[0] ?? ''] || '—'
  }

  function time(iso: string | null): string {
    if (!iso || !event) return '—'
    return formatTime(iso, i18n.language, event.timezone)
  }

  return (
    <>
      <AdminHeader
        title={t('admin.section.program')}
        description={t('admin.program.description')}
      />

      <div className="grid gap-6">
        {/* L'adresse de la télé de la salle, clé comprise : réservée à qui
            règle l'édition (la clé ouvre l'écran à n'importe qui). */}
        {can('event.settings.update') ? <ScreenLinkCard eventId={eventId} /> : null}

        <CollectionEditor<ProgramItem>
          title={t('admin.program.items')}
          description={t('admin.program.itemsHint')}
          items={items.items}
          busy={items.busy}
          error={items.error}
          emptyLabel={t('admin.program.noItems')}
          createLabel={t('admin.program.addItem')}
          onDelete={(item) => items.remove.mutate({ id: item.id })}
          // Une étape déjà jouée fait partie de l'histoire de la soirée : la
          // supprimer effacerait l'heure réelle à laquelle elle a eu lieu.
          canDelete={(item) => item.status === 'scheduled'}
          summarize={(item) => ({
            title: label(item.title_i18n),
            subtitle: [
              t(`admin.program.kind.${item.kind}`),
              item.host,
              item.location,
            ]
              .filter(Boolean)
              .join(' · '),
            value: time(item.starts_at),
            dimmed: !item.is_public,
            badges: [
              ...(item.is_public ? [] : [{ label: t('admin.program.internal') } as const]),
              ...(item.status === 'running'
                ? [{ label: t('admin.program.status.running'), tone: 'success' } as const]
                : []),
              ...(item.status === 'done'
                ? [{ label: t('admin.program.status.done') } as const]
                : []),
            ],
          })}
          renderForm={(item, close) => (
            <ProgramItemForm
              item={item}
              activities={activities.items}
              activityLabel={label}
              busy={items.busy}
              onCancel={close}
              onSubmit={(payload) => {
                const done = { onSuccess: () => close() }
                if (item) items.update.mutate({ id: item.id, ...payload }, done)
                else items.create.mutate(payload, done)
              }}
            />
          )}
        />

        <CollectionEditor<Activity>
          title={t('admin.program.activities')}
          description={t('admin.program.activitiesHint')}
          items={activities.items}
          busy={activities.busy}
          error={activities.error}
          emptyLabel={t('admin.program.noActivities')}
          createLabel={t('admin.program.addActivity')}
          onDelete={(activity) => activities.remove.mutate({ id: activity.id })}
          summarize={(activity) => ({
            title: label(activity.name_i18n),
            subtitle: [activity.category, activity.host].filter(Boolean).join(' · '),
            value: activity.duration_minutes
              ? t('admin.program.minutes', { count: activity.duration_minutes })
              : '',
            dimmed: !activity.is_active,
          })}
          renderForm={(activity, close) => (
            <ActivityForm
              activity={activity}
              busy={activities.busy}
              onCancel={close}
              onSubmit={(payload) => {
                const done = { onSuccess: () => close() }
                if (activity) activities.update.mutate({ id: activity.id, ...payload }, done)
                else activities.create.mutate(payload, done)
              }}
            />
          )}
        />

        {can('program.item.read') ? (
          <CakeMomentPanel
            eventId={eventId}
            timezone={event?.timezone ?? 'Europe/Berlin'}
            programItems={items.items}
            label={label}
            canEdit={can('program.cake.update')}
          />
        ) : null}
      </div>
    </>
  )
}

function ProgramItemForm({
  item,
  activities,
  activityLabel,
  busy,
  onSubmit,
  onCancel,
}: {
  item: ProgramItem | null
  activities: Activity[]
  activityLabel: (value: I18n) => string
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [title, setTitle] = useState<I18nValue>(item?.title_i18n ?? {})
  const [description, setDescription] = useState<I18nValue>(item?.description_i18n ?? {})
  const [kind, setKind] = useState<ProgramItemKind>(item?.kind ?? 'slot')
  const [refId, setRefId] = useState(item?.ref_id ?? '')
  const [startsAt, setStartsAt] = useState(toLocalInput(item?.starts_at ?? null))
  const [endsAt, setEndsAt] = useState(toLocalInput(item?.ends_at ?? null))
  const [location, setLocation] = useState(item?.location ?? '')
  const [host, setHost] = useState(item?.host ?? '')
  const [isPublic, setIsPublic] = useState(item?.is_public ?? true)

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        <I18nField label={t('admin.program.title')} value={title} onChange={setTitle} />
        <I18nField
          label={t('admin.food.descriptionField')}
          value={description}
          onChange={setDescription}
        />
        <SelectField<ProgramItemKind>
          label={t('admin.program.kindLabel')}
          value={kind}
          onChange={setKind}
          options={KINDS.map((value) => ({ value, label: t(`admin.program.kind.${value}`) }))}
        />

        {/* Une étape « animation » pointe vers une activité du catalogue :
            l'écran de la salle en tire les règles et le matériel. */}
        {kind === 'activity' ? (
          <SelectField
            label={t('admin.program.linkedActivity')}
            value={refId}
            onChange={setRefId}
            options={[
              { value: '', label: t('admin.program.noActivity') },
              ...activities.map((activity) => ({
                value: activity.id,
                label: activityLabel(activity.name_i18n),
              })),
            ]}
          />
        ) : null}

        <TextField
          label={t('admin.program.startsAt')}
          type="datetime-local"
          value={startsAt}
          onChange={setStartsAt}
        />
        <TextField
          label={t('admin.program.endsAt')}
          type="datetime-local"
          value={endsAt}
          onChange={setEndsAt}
        />
        <TextField label={t('admin.program.location')} value={location} onChange={setLocation} />
        <TextField label={t('admin.program.host')} value={host} onChange={setHost} />
        <SwitchField
          label={t('admin.program.isPublic')}
          hint={t('admin.program.isPublicHint')}
          value={isPublic}
          onChange={setIsPublic}
        />
      </div>

      <FormActions
        busy={busy}
        onCancel={onCancel}
        onSubmit={() =>
          onSubmit({
            title_i18n: title,
            description_i18n: description,
            kind,
            ref_id: kind === 'activity' ? refId || null : null,
            starts_at: fromLocalInput(startsAt),
            ends_at: fromLocalInput(endsAt),
            location: location || null,
            host: host || null,
            is_public: isPublic,
          })
        }
      />
    </div>
  )
}

function ActivityForm({
  activity,
  busy,
  onSubmit,
  onCancel,
}: {
  activity: Activity | null
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState<I18nValue>(activity?.name_i18n ?? {})
  const [description, setDescription] = useState<I18nValue>(activity?.description_i18n ?? {})
  const [rules, setRules] = useState<I18nValue>(activity?.rules_i18n ?? {})
  const [equipment, setEquipment] = useState<I18nValue>(activity?.equipment_i18n ?? {})
  const [prize, setPrize] = useState<I18nValue>(activity?.prize_i18n ?? {})
  const [duration, setDuration] = useState<number | null>(activity?.duration_minutes ?? null)
  const [host, setHost] = useState(activity?.host ?? '')
  const [participants, setParticipants] = useState<number | null>(
    activity?.participants_count ?? null,
  )
  const [category, setCategory] = useState(activity?.category ?? '')
  const [active, setActive] = useState(activity?.is_active ?? true)

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        <I18nField label={t('admin.food.name')} value={name} onChange={setName} />
        <I18nField
          label={t('admin.food.descriptionField')}
          value={description}
          onChange={setDescription}
        />
        <I18nField label={t('admin.program.rules')} multiline value={rules} onChange={setRules} />
        <I18nField
          label={t('admin.program.equipment')}
          multiline
          value={equipment}
          onChange={setEquipment}
        />
        <I18nField label={t('admin.program.prize')} value={prize} onChange={setPrize} />
        <NumberField
          label={t('admin.program.duration')}
          value={duration}
          min={1}
          max={600}
          onChange={setDuration}
        />
        <TextField label={t('admin.program.host')} value={host} onChange={setHost} />
        <NumberField
          label={t('admin.program.participants')}
          value={participants}
          min={0}
          onChange={setParticipants}
        />
        <TextField
          label={t('admin.program.category')}
          hint={t('admin.program.categoryHint')}
          value={category}
          onChange={setCategory}
        />
        <SwitchField
          label={t('admin.active')}
          hint={t('admin.activeHint')}
          value={active}
          onChange={setActive}
        />
      </div>

      <FormActions
        busy={busy}
        onCancel={onCancel}
        onSubmit={() =>
          onSubmit({
            name_i18n: name,
            description_i18n: description,
            rules_i18n: rules,
            equipment_i18n: equipment,
            prize_i18n: prize,
            duration_minutes: duration,
            host: host || null,
            participants_count: participants,
            category: category || null,
            is_active: active,
          })
        }
      />
    </div>
  )
}

/** ISO → `<input type="datetime-local">`, et retour. Voir `EventPage`. */
function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function fromLocalInput(value: string): string | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}
